import { useMutation, useQuery } from '@apollo/client'
import classNames from 'classnames'
import { useCallback, useMemo } from 'react'
import { useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'

import { Button, Card } from '../../components'
import { Paths } from '../../config/routes'
import { useUser } from '../../contexts/user/user-context'
import { STATUS } from '../../lib/constants'
import { UpdateLoanApplication } from '../loans/queries'
import { GetLoan } from './queries'

export function Loan() {
    let { id } = useParams<{ id: string }>()
    const { userProfile } = useUser()
    const [updateLoanApplicationId] = useMutation(UpdateLoanApplication)
    const history = useHistory()
    const unlink = useCallback(async () => {
        await updateLoanApplicationId({
            variables: { input: { loanApplicationId: null } },
        })
        history.push(Paths.DASHBOARD)
    }, [updateLoanApplicationId, history])

    const payableAddress = useMemo(() => {
        // @ts-ignore
        const address = userProfile?.getUserProfile?.payableAddress ?? null
        if (address && address.length && address.length > 5) {
            return `${address.substring(0, 2)}...${address.substring(
                address.length - 4,
                address.length,
            )}`
        }
        if (address && address.length) {
            return address
        }

        return null
    }, [userProfile])

    const email = useMemo(() => {
        // @ts-ignore
        return userProfile?.getUserProfile?.email ?? null
    }, [userProfile])

    const { data: loan, loading } = useQuery(GetLoan, {
        variables: { id },
        skip: !id,
    })

    const status = useMemo(() => {
        if (!loading && loan) {
            // Maybe we need a parseInt here
            switch (loan.getOneLoanApplicationFromNode.data.status) {
                case STATUS.PENDING:
                    return STATUS.PENDING
                case STATUS.APPROVED:
                    return STATUS.APPROVED
                case STATUS.REJECTED:
                    return STATUS.REJECTED
                case STATUS.CANCELLED:
                    return STATUS.CANCELLED
                default:
                    return STATUS.PENDING
            }
        }
    }, [loan, loading])

    const loanStatsStyles = classNames('text-black text-sm font-light')
    return (
        <div className='grid grid-cols-8 gap-16 pt-12 '>
            <div className='col-span-4 col-start-3'>
                {status === STATUS.PENDING && (
                    <Card className='w-full h-full py-8'>
                        <h2>Loan application pending</h2>
                        <p className='text-black font-light'>
                            Your loan is waiting to be approved by underwriters.
                            You will be notified by email when your loan decison
                            has been made. You will be required to sign loan
                            terms once your loan has been approved.
                        </p>
                        <Link to={Paths.DASHBOARD}>
                            <Button className='mt-4 py-4'>Dashboard</Button>
                        </Link>
                    </Card>
                )}
                {status === STATUS.APPROVED && (
                    <Card className='w-full h-full py-8'>
                        <h2>Loan Approved!</h2>
                        <p className='text-black font-light'>
                            Your loan has been approved and loan terms have been
                            sent to your email{' '}
                            <span className='text-linkYellow font-semibold'>
                                {email}
                            </span>{' '}
                            for signature. Please review the document carefully.
                            Once signed your loan will be sent to your wallet:{' '}
                            <span className='text-linkYellow font-semibold'>
                                {payableAddress}
                            </span>
                        </p>
                        <Button className='mt-4 py-4' onClick={unlink}>
                            Accept the loan
                        </Button>
                    </Card>
                )}

                {status === STATUS.REJECTED && (
                    <Card className='w-full h-full py-8'>
                        <h2>Loan rejected</h2>
                        <p className='text-black font-light'>
                            Your loan has been rejected. Please try later.
                        </p>

                        <Button className='mt-4 py-4' onClick={unlink}>
                            Dashboard
                        </Button>
                    </Card>
                )}
                {status === STATUS.SENT && (
                    <Card className='w-full h-full py-8'>
                        <h2>Loan sent</h2>
                        <p className='text-black font-light w-4/5'>
                            Your loan has been sent to your wallet:{' '}
                            <span className='text-linkYellow font-semibold'>
                                {payableAddress}
                            </span>{' '}
                            awith the following transaction id{' '}
                            <span className='text-linkYellow font-semibold'>
                                0x...gt6R
                            </span>
                            . Your loan terms and first repayment are as
                            follows:
                        </p>
                        <div>
                            <h3>Your loan</h3>
                            <div className='grid grid-cols-2'>
                                <div>
                                    <p className={loanStatsStyles}>
                                        Loan amount: ${loan.data.data.amount}
                                    </p>
                                    <p className={loanStatsStyles}>
                                        Repayment date: 28th of the month
                                    </p>
                                    <p className={loanStatsStyles}>
                                        First repayment: 28th October 2021
                                    </p>
                                    <p className={loanStatsStyles}>
                                        Repayment amount: $447.92
                                    </p>
                                </div>
                                <div>
                                    <p className={loanStatsStyles}>
                                        Loan term:{' '}
                                        <span className='text-black font-normal'>
                                            {loan.data.data.term} months
                                        </span>
                                    </p>
                                    <p className={loanStatsStyles}>
                                        Interest rate:{' '}
                                        <span className='text-black font-normal'>
                                            {loan.data.data.interest_rate * 100}
                                            % APY
                                        </span>
                                    </p>
                                    <p className={loanStatsStyles}>
                                        Collateral:{' '}
                                        <span className='text-black font-normal'>
                                            None required
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Link to={Paths.DASHBOARD}>
                            <Button className='mt-4 py-4'>Dashboard</Button>
                        </Link>
                    </Card>
                )}
            </div>
        </div>
    )
}
